import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { eventsApi, type RaceEvent } from '@/api/events';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { commonT } from '@/i18n';
import { eventsT } from '@/i18n/messages/events';
import { Pencil, Radio, Search, Trash2, X } from 'lucide-react';

/// Status EXIBIDO ≠ status persistido (#630): 'running' saiu do
/// event.status no multi-day Fase 1 (virou derivado dos dias, servido em
/// `hasRunningDay`). A lista mostra "Em andamento" quando um dia está
/// rodando, mas nenhum código deve comparar `ev.status === 'running'`.
type EventDisplayStatus = RaceEvent['status'] | 'running';

const displayStatus = (ev: RaceEvent): EventDisplayStatus =>
  ev.status === 'published' && ev.hasRunningDay ? 'running' : ev.status;

/// Cores dos chips de status. Mesmas cores usadas no live admin pra
/// consistência visual (verde = ativo, cinza = encerrado, amarelo =
/// aguardando, slate = setup). O rótulo vem do dicionário (`eventsT().status`).
const STATUS_CHIP_CLASS: Record<EventDisplayStatus, string> = {
  draft: 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/40',
  published: 'bg-amber-500/20 text-amber-800 dark:text-amber-300 border-amber-500/50',
  running: 'bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 border-emerald-500/50',
  finished: 'bg-zinc-500/15 text-zinc-700 dark:text-zinc-300 border-zinc-500/40',
};

/// Ordem dos filtros de status (segue o ciclo de vida do evento).
const STATUS_ORDER: EventDisplayStatus[] = ['draft', 'published', 'running', 'finished'];

type StatusFilter = EventDisplayStatus | 'all';

function filterChipClass(active: boolean): string {
  return `px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
    active
      ? 'bg-primary text-primary-foreground border-primary'
      : 'bg-background text-muted-foreground border-border hover:bg-muted'
  }`;
}

export function EventsPage() {
  const t = eventsT();
  const common = commonT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['events'], queryFn: eventsApi.list });

  // Publicar: draft → published. Encerrar: running → finished. Compartilham
  // o mesmo PUT /api/events/{id} via setStatus. "Disparar largada" saiu
  // desta lista — fica no painel ao vivo (/events/:id/live), que suporta
  // multi-day (disparar dia 2+).
  const publish = useMutation({
    mutationFn: (id: string) => eventsApi.setStatus(id, 'published'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });
  const finish = useMutation({
    mutationFn: (id: string) => eventsApi.setStatus(id, 'finished'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => eventsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });

  const inflight = (id: string) =>
    (publish.isPending && publish.variables === id) ||
    (finish.isPending && finish.variables === id) ||
    (remove.isPending && remove.variables === id);

  // Confirmação por ação — guarda o eventId pendente. Listagem multi-row
  // exige saber qual card disparou o dialog.
  const [confirmFinishId, setConfirmFinishId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Busca (nome/slug) + filtro por status — client-side, a lista já vem
  // inteira do /api/events. Filtragem barata; ordem preservada do backend.
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const filtered = useMemo(() => {
    const all = list.data ?? [];
    const q = search.trim().toLowerCase();
    return all.filter((ev) => {
      if (statusFilter !== 'all' && displayStatus(ev) !== statusFilter) return false;
      if (!q) return true;
      return ev.name.toLowerCase().includes(q) || (ev.slug ?? '').toLowerCase().includes(q);
    });
  }, [list.data, search, statusFilter]);
  const total = list.data?.length ?? 0;
  const hasActiveFilter = search.trim() !== '' || statusFilter !== 'all';

  // Sem pré-criar o evento. O wizard cria no submit do passo 1 — alinhado
  // com o comentário em Step1EventBasics ("Não cria evento até clicar Salvar")
  // e evita rascunho órfão "Novo evento" no DB cada vez que clicam aqui.
  // Bônus: o input do nome começa vazio em vez de carregar "Novo evento"
  // como valor (que o usuário precisava apagar antes de digitar).

  return (
    <div>
      {/* #936 — Negócio, Usuários, e-mail, idioma e Sair moraram aqui até o
          menu lateral (`AppShell`); o cabeçalho ficou só com o título. */}
      <header className="border-b">
        <div className="container mx-auto py-3 sm:py-4">
          <h1 className="text-lg sm:text-xl font-semibold">{t.title}</h1>
        </div>
      </header>

      <main className="container mx-auto py-6 sm:py-10 space-y-6 max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-muted-foreground text-sm">{t.intro}</p>
          <Button onClick={() => navigate('/events/new')} className="w-full sm:w-auto">
            {t.newEvent}
          </Button>
        </div>

        {list.isLoading && <Spinner />}
        {list.isError && (
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-destructive">{t.loadFailed((list.error as Error).message)}</p>
            </CardContent>
          </Card>
        )}
        {list.data && total === 0 && (
          <Card>
            <CardHeader>
              <CardTitle>{t.emptyTitle}</CardTitle>
              <CardDescription>{t.emptyBody}</CardDescription>
            </CardHeader>
          </Card>
        )}

        {total > 0 && (
          <div className="space-y-3">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t.searchPlaceholder}
                aria-label={t.searchLabel}
                className="pl-9 pr-9"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label={t.clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setStatusFilter('all')}
                className={filterChipClass(statusFilter === 'all')}
              >
                {t.filterAll}
              </button>
              {STATUS_ORDER.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={filterChipClass(statusFilter === s)}
                >
                  {t.status[s]}
                </button>
              ))}
              {hasActiveFilter && (
                <span className="ml-auto text-xs text-muted-foreground">
                  {t.filteredCount(filtered.length, total)}
                </span>
              )}
            </div>
          </div>
        )}

        {total > 0 && filtered.length === 0 && (
          <Card>
            <CardContent className="pt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">{t.noMatch}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearch('');
                  setStatusFilter('all');
                }}
              >
                {t.clearFilters}
              </Button>
            </CardContent>
          </Card>
        )}

        <ul className="space-y-3">
          {filtered.map((ev) => {
            // Status desconhecido (backend mais novo que o painel) cai no
            // visual de rascunho, como sempre caiu.
            const status = displayStatus(ev);
            const shown: EventDisplayStatus = status in STATUS_CHIP_CLASS ? status : 'draft';
            const busy = inflight(ev.id);
            return (
              <li key={ev.id}>
                <Card>
                  <CardContent className="pt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="flex flex-1 min-w-0 items-center gap-3">
                      {ev.logoUrl ? (
                        <img src={ev.logoUrl} alt="" className="h-10 w-10 rounded-md object-cover border" />
                      ) : (
                        <div
                          className="h-10 w-10 rounded-md border"
                          style={{ backgroundColor: ev.primaryColor ?? '#0EA5E9' }}
                          aria-hidden
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium break-words">{ev.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className={`inline-flex shrink-0 items-center whitespace-nowrap px-2 py-0.5 rounded-full border text-[11px] font-medium ${STATUS_CHIP_CLASS[shown]}`}
                          >
                            {t.status[shown]}
                          </span>
                          {ev.trackersEnabled && (
                            <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap px-2 py-0.5 rounded-full border text-[11px] font-medium bg-sky-500/15 text-sky-800 dark:text-sky-300 border-sky-500/40">
                              <Radio className="h-3 w-3" aria-hidden />
                              {t.trackersChip}
                            </span>
                          )}
                          <p className="text-xs text-foreground/70 truncate">
                            {ev.slug ? `/${ev.slug}` : t.noSlug}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                      {/* Botão contextual por estado (project_mass_start_plan):
                          draft → Publicar, published → Encerrar, finished →
                          nada. Encerrável = publicado e não encerrado (regra
                          que o PUT já aceita) — condicionar a 'running' deixou
                          o botão morto por meses, pois event.status nunca mais
                          assume esse valor (#630). "Disparar largada" mora no
                          painel ao vivo. */}
                      {ev.status === 'draft' && (
                        <Button
                          size="sm"
                          onClick={() => publish.mutate(ev.id)}
                          disabled={busy}
                        >
                          {busy ? t.publishing : t.publish}
                        </Button>
                      )}
                      {ev.status === 'published' && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setConfirmFinishId(ev.id)}
                          disabled={busy}
                        >
                          {busy ? t.finishing : t.finish}
                        </Button>
                      )}
                      <Button asChild variant="outline" size="sm">
                        {/* Sem passo: o wizard cai na 1ª etapa pendente (#937). */}
                        <Link to={`/events/${ev.id}/wizard`}>
                          <Pencil className="h-4 w-4 mr-1.5" />
                          {common.edit}
                        </Link>
                      </Button>
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/events/${ev.id}/live`}>{t.organizerDashboard}</Link>
                      </Button>
                      {/* #934 — atalho pro resultado sem passar pelo live. É o
                          que o organizador abre DEPOIS da prova, quando o mapa
                          já não interessa e ele quer a lista pra publicar. */}
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/events/${ev.id}/results`}>{t.results}</Link>
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => setConfirmDeleteId(ev.id)}
                        disabled={busy}
                        aria-label={t.deleteEvent}
                        title={t.deleteEvent}
                      >
                        {busy && remove.variables === ev.id ? <Spinner /> : <Trash2 className="h-4 w-4" />}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      </main>

      <ConfirmDialog
        open={confirmFinishId != null}
        onOpenChange={(o) => !o && setConfirmFinishId(null)}
        title={t.confirmFinishTitle}
        description={t.confirmFinishBody}
        confirmLabel={t.finish}
        destructive
        onConfirm={() => confirmFinishId && finish.mutate(confirmFinishId)}
      />
      <ConfirmDialog
        open={confirmDeleteId != null}
        onOpenChange={(o) => !o && setConfirmDeleteId(null)}
        title={t.confirmDeleteTitle}
        description={t.confirmDeleteBody}
        confirmLabel={common.delete}
        destructive
        onConfirm={() => confirmDeleteId && remove.mutate(confirmDeleteId)}
      />
    </div>
  );
}
